import { Button } from 'antd'
import Image from 'next/image'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import useUrlControl from '@/components/public-page/hook/use-url-control'
import { CHROME_EXTENSION_STORE_URL } from '@/constants/chrome-extension'
import { Page } from '@/hooks/use-authorization/constants'
import routes from '@/hooks/use-authorization/routes'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useIntl } from '@/i18n/hooks/use-intl'
import bgSrc from '@/public/assets/billboard/background.png'
import bgSrcMobile from '@/public/assets/billboard/mobile.png'
import { ampli, ClicksRegisterProperties } from '@/utils/ampli'

interface RecommendationSectionProps {
  registerSearchParams?: typeof routes.register.searchParamsInputType
  from?: ClicksRegisterProperties['from']
}

const RecommendationSection: FunctionComponent<RecommendationSectionProps> = ({
  registerSearchParams,
  from = 'home',
}) => {
  const { formatMessage } = useIntl()
  const { getAppUrl } = useUrlControl()
  const breakpoint = useBreakpoint()

  return (
    <Background>
      <Wrapper>
        <Title>
          <div>{formatMessage({ id: 'landing:recommendation_title_1' })}</div>
          <div>{formatMessage({ id: 'landing:recommendation_title_2' })}</div>
        </Title>
        <Buttons>
          <Button
            href={getAppUrl(Page.Register, {
              from,
              ...registerSearchParams,
            })}
            size={breakpoint === 'PC' ? 'large' : 'small'}
            target='_blank'
            type='primary'
            onClick={(): void => {
              ampli.clicksRegister({
                from,
              })
            }}
          >
            {formatMessage({ id: 'general:navbar_text_signup' })}
          </Button>
          <Button
            danger
            href={CHROME_EXTENSION_STORE_URL}
            size={breakpoint === 'PC' ? 'large' : 'small'}
            target='_blank'
            onClick={(): void => {
              ampli.visitChromeExtensionWebstore({
                from,
              })
            }}
          >
            {formatMessage({ id: 'landing:extension_download_act' })}
          </Button>
        </Buttons>
        <MobileBackgroundImage
          alt='image'
          height={750}
          src={bgSrcMobile}
          width={314}
        />
      </Wrapper>
    </Background>
  )
}

const Title = styled.div`
  padding: 0 20px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.44;
  color: ${({ theme }): string => theme.colors.text.primary};

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const Background = styled.div`
  background: ${({ theme }): string => theme.colors.ai.purpleGradient2};
`

const Wrapper = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  padding: 140px 24px 180px;
  background-image: url(${bgSrc});
  background-position: center;
  background-size: cover;

  ${Title} {
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    background-image: unset;
    padding: 40px 0 0;

    ${Title} {
      margin-bottom: 24px;
    }

    ${Buttons} {
      margin-bottom: 24px;
    }
  }
`

const MobileBackgroundImage = styled(Image)`
  object-fit: contain;
  height: auto;

  @media (min-width: 768px) {
    display: none;
  }
`

export default RecommendationSection
